import PropTypes from 'prop-types';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

// material-ui
import { makeStyles, useTheme } from '@material-ui/styles';
import { AppBar, CssBaseline, Toolbar, useMediaQuery } from '@material-ui/core';

// third-party
import clsx from 'clsx';

// project imports
import Breadcrumbs from './../../ui-component/extended/Breadcrumbs';
import Header from './Header';
import Sidebar from './Sidebar';
import Regi from './../../ui-component/Regi';
import {adminMenuItems} from './../../menu-items';
import { drawerWidth } from '../../store/constant';
import { SET_MENU } from './../../store/actions';
import { saveLogoutSnapshot, getSidebarWidth, setSidebarWidth } from '../../utils/userPrefs';
import { RegiPageProvider } from '../../utils/regiContext';

// assets
import { IconChevronRight } from '@tabler/icons';

// style constant
const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        background: `
            radial-gradient(circle at 20% 5%, rgba(124, 77, 255, 0.13), transparent 42%),
            radial-gradient(circle at 86% 12%, rgba(255, 120, 160, 0.13), transparent 42%),
            radial-gradient(circle at 22% 88%, rgba(64, 196, 255, 0.10), transparent 48%),
            linear-gradient(135deg, #f7f9ff 0%, #fff7fb 48%, #f4fbff 100%)
        `,
        backgroundAttachment: 'fixed',
    },
    appBar: {
        backgroundColor: 'transparent !important',
        boxShadow: 'none !important',
        borderBottom: 'none',
        // Drawer closed: on desktop (persistent variant — see Sidebar) the sidebar no longer
        // takes up layout space, so the header should span full width like <main> does via
        // classes.content. Below `md` the sidebar is a `temporary` (overlay) drawer that never
        // pushes layout, so the header stays full-width there regardless of open/closed.
        [theme.breakpoints.up('md')]: {
            marginLeft: 0,
            width: '100%',
        },
    },
    appBarWidth: {
        // No width transition while actively dragging the sidebar — see the matching comment
        // in Sidebar/index.js's hamburger button; same lag-behind-the-cursor reason.
        transition: ({ dragging }) => (dragging ? 'none' : theme.transitions.create('width')),
        backgroundColor: 'transparent !important',
        boxShadow: 'none !important',
        borderBottom: 'none',
        // Drawer open on desktop: match classes.contentShift's pushed-right layout.
        [theme.breakpoints.up('md')]: {
            marginLeft: ({ width }) => width,
            width: ({ width }) => `calc(100% - ${width}px)`,
        },
    },
    content: {
        ...theme.typography.mainContent,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        transition: ({ dragging }) =>
            dragging
                ? 'none'
                : theme.transitions.create('margin', {
                      easing: theme.transitions.easing.sharp,
                      duration: theme.transitions.duration.leavingScreen
                  }),
        [theme.breakpoints.up('md')]: {
            marginLeft: ({ width }) => -(width - 20),
            width: ({ width }) => `calc(100% - ${width}px)`
        },
        [theme.breakpoints.down('md')]: {
            marginLeft: '20px',
            width: ({ width }) => `calc(100% - ${width}px)`,
            padding: '16px'
        },
        [theme.breakpoints.down('sm')]: {
            marginLeft: '10px',
            width: ({ width }) => `calc(100% - ${width}px)`,
            padding: '16px',
            marginRight: '10px'
        }
    },
    contentShift: {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen
        }),
        marginLeft: 0,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        [theme.breakpoints.down('md')]: {
            marginLeft: '20px'
        },
        [theme.breakpoints.down('sm')]: {
            marginLeft: '10px'
        }
    }
}));

//-----------------------|| MAIN LAYOUT ||-----------------------//

const MainLayout = ({ children }) => {
    const theme = useTheme();
    const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));

    // Handle left drawer
    const leftDrawerOpened = useSelector((state) => state.customization.opened);
    const dispatch = useDispatch();
    const handleLeftDrawerToggle = () => {
        dispatch({ type: SET_MENU, opened: !leftDrawerOpened });
    };

    React.useEffect(() => {
        dispatch({ type: SET_MENU, opened: !matchDownMd });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [matchDownMd]);

    // Sidebar width: draggable via Sidebar's resize handle, remembered per user. Starts at the
    // default and is corrected once sessionUserId is known (same pattern MenuList uses for
    // getPinnedProjects — userId isn't necessarily ready on first render).
    const sessionUserId = useSelector((s) => s.authReducer?.user?.id);
    const [sidebarWidth, setSidebarWidthState] = React.useState(drawerWidth);
    const [sidebarDragging, setSidebarDragging] = React.useState(false);
    React.useEffect(() => {
        if (!sessionUserId) return;
        const stored = getSidebarWidth(sessionUserId);
        if (stored) setSidebarWidthState(stored);
    }, [sessionUserId]);
    // Live width while dragging (updates on every mousemove so <main>/<AppBar> track the cursor).
    const handleSidebarWidthChange = (next) => {
        setSidebarDragging(true);
        setSidebarWidthState(next);
    };
    // Fires once on mouseup: persists the final width.
    const handleSidebarWidthCommit = (final) => {
        setSidebarDragging(false);
        setSidebarWidthState(final);
        if (sessionUserId) setSidebarWidth(sessionUserId, final);
    };

    const classes = useStyles({ width: sidebarWidth, dragging: sidebarDragging });

    // <main>/<AppBar>'s marginLeft and width are set here as inline style, not left to
    // classes.content/classes.contentShift's CSS alone. Reason: once a JSS rule (content,
    // appBarWidth) contains a function value — which it now does, since it depends on the
    // `width` prop — @material-ui/styles v5 inserts it into a separately-timed stylesheet from a
    // plain-value rule declared right next to it in the same object (contentShift's plain
    // `marginLeft: 0`). That reordered the cascade so contentShift's override stopped reliably
    // beating content's negative margin, even though contentShift is declared AFTER content
    // below. Confirmed in production: computed style showed marginLeft: -240px on <main> while
    // the drawer was visibly open, so the sidebar visually covered the left edge of the page
    // content. Inline style always wins regardless of any CSS-in-JS library's internal
    // rule-insertion order, so it's the source of truth here; the classes below still supply the
    // transition and the down('md')/down('sm') mobile values (isDesktopLayout is false there, so
    // this doesn't apply — the mobile `temporary` drawer never pushes layout anyway).
    const isDesktopLayout = !matchDownMd;
    const mainInlineLayout = isDesktopLayout
        ? { marginLeft: leftDrawerOpened ? 0 : -(sidebarWidth - 20), width: `calc(100% - ${sidebarWidth}px)` }
        : undefined;
    const appBarInlineLayout =
        isDesktopLayout && leftDrawerOpened ? { marginLeft: sidebarWidth, width: `calc(100% - ${sidebarWidth}px)` } : undefined;

    // Continuously refresh the logout snapshot so closing the browser still
    // restores the user's last location on next login. Cleared on token kick
    // (fetchHelper softLogout) so a 401 doesn't bounce the user back to a
    // half-loaded page.
    const location = useLocation();
    React.useEffect(() => {
        if (!sessionUserId) return;
        if (!location || typeof location.pathname !== 'string') return;
        saveLogoutSnapshot(sessionUserId, {
            pathname: location.pathname,
            search: location.search || '',
        });
    }, [sessionUserId, location.pathname, location.search]);

    return (
        <RegiPageProvider>
        <div className={classes.root}>
            <CssBaseline />
            {/* header */}
            <AppBar
                enableColorOnDark
                position="fixed"
                color="inherit"
                elevation={0}
                // ml/width used to be hardcoded here regardless of leftDrawerOpened, so the
                // header never resized when the sidebar was collapsed (unlike <main>, which
                // correctly toggles between classes.content/contentShift). The actual values now
                // live in the appBar/appBarWidth classes below, toggled the same way <main> does;
                // this base just sets a safe full-width default for the mobile (`temporary`
                // drawer) breakpoint, which neither class overrides.
                sx={{
                    zIndex: 1200,
                    ml: 0,
                    width: '100%',
                }}
                style={appBarInlineLayout}
                className={leftDrawerOpened ? classes.appBarWidth : classes.appBar}
            >
                <Toolbar sx={{ minHeight: '72px !important', height: 72 }}>
                    <Header handleLeftDrawerToggle={handleLeftDrawerToggle}/>
                </Toolbar>
            </AppBar>

            {/* drawer */}
            <Sidebar
                drawerOpen={leftDrawerOpened}
                drawerToggle={handleLeftDrawerToggle}
                width={sidebarWidth}
                onWidthChange={handleSidebarWidthChange}
                onWidthCommit={handleSidebarWidthCommit}
            />

            {/* main content */}
            <main
                style={{
                    paddingTop: '72px',
                    backgroundColor: 'transparent',
                    background: `
                        radial-gradient(circle at 20% 5%, rgba(124, 77, 255, 0.13), transparent 42%),
                        radial-gradient(circle at 86% 12%, rgba(255, 120, 160, 0.13), transparent 42%),
                        radial-gradient(circle at 22% 88%, rgba(64, 196, 255, 0.10), transparent 48%),
                        linear-gradient(135deg, #f7f9ff 0%, #fff7fb 48%, #f4fbff 100%)
                    `,
                    backgroundAttachment: 'fixed',
                    ...mainInlineLayout,
                }}
                className={clsx([
                    classes.content,
                    {
                        [classes.contentShift]: leftDrawerOpened
                    }
                ])}
            >
                {/* <Main open={leftDrawerOpened}> */}
                {/* breadcrumb */}
                <Breadcrumbs separator={IconChevronRight} navigation={adminMenuItems} icon title rightAlign />
                <div>{children}</div>
                {/* </Main> */}
            </main>
            {/* <Customization /> */}
            <Regi />
        </div>
        </RegiPageProvider>
    );
};

MainLayout.propTypes = {
    children: PropTypes.node
};

export default MainLayout;
